<template>
  <div class="content-container">
    <div class="action-container">
      <div class="left">
        <div class="v-select-container">
          <div class="title">{{ t('bi.source') }}</div>
          <div class="select">
            <v-select
              class="v-select"
              v-model="sourceList"
              :options="sourceListOptions"
              :placeholder="t('bi.select_source')"
              multiple
              :searchable="false"
              :close-on-select="false"
              deselect-from-dropdown
            >
              <template #option="option">
                <div class="form-check" style="pointer-events: none">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    :checked="sourceList.find((el) => option.value === el.value) ? true : false"
                    readonly
                  />
                  <div class="form-check-label font-f3">{{ t(`bi.${option.label}`) }}</div>
                </div>
              </template>
              <template #open-indicator="{ attributes }">
                <span v-bind="attributes"><down-ward-icon fill="#007fff" /></span>
              </template>
            </v-select>
          </div>
        </div>
      </div>
      <div class="right">
        <div class="filters">
          <div class="date-range-picker">
            <choco-date-range-picker
              v-if="relativeModel"
              class="choco-date-range-picker"
              v-model:start="startDate"
              v-model:end="endDate"
              :relative-options="['today', 'P7D', 'P15D', 'P30D', 'P90D']"
              v-model:relative-model="relativeModel"
              :format="'dd MMM yyyy'"
              :relative-title="t('date_picker.date_range')"
              relative-title-class="font-f1"
              @click="onClickDateSelect"
              @hide="() => {}"
              :is-show-apply-button="true"
            >
              <template v-slot:input="props">
                <date-range-display-input class="input" v-bind="props" prepend-icon />
              </template>
            </choco-date-range-picker>
          </div>
          <div class="bi-reset-date-picker" @click="onResetDate" v-bt-tooltips :title="t('bi.reset')">
            <reset-icon />
          </div>
          <button class="btn btn-outline-primary d-flex align-items-center bi-filter-btn text-nowrap" @click="onClickApplyFilter">
            {{ t('bi.apply') }}
          </button>
        </div>
        <div class="chart-action">
          <ExportPDFButton
            :loading="loading.exportPdf"
            @click="onExportPreview"
            v-permission="$can('portal-cdp', 'cdp-portal-dashboard-downloadpdf')"
          />
          <div class="bi-action-wrp" v-permission="$can('portal-cdp', 'cdp-portal-dashboard-addgraph')">
            <template v-if="!loading.tabs && (modalOpen.addTab || modalOpen.editTabItem)">
              <button class="btn btn-outline-primary d-flex align-items-center create-bi-button" type="button" disabled>
                <span class="">{{ t('segment.loading') }}...</span>
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </button>
            </template>
            <template v-else>
              <button
                class="btn btn-outline-primary d-flex align-items-center create-bi-button"
                @click="modalOpen.addAnalyticsChart = true"
                v-bt-tooltips
                :title="t('bi.add_chart')"
              >
                <div class="add-icon">
                  <add-icon />
                </div>
                <div>{{ t('bi.add_chart') }}</div>
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="bi-overview-container">
      <template v-if="!loading.layout">
        <grid-layout
          v-if="layout.length > 0"
          v-model:layout="layout"
          :col-num="GIRD_LAYOUT.COL_NUMBERS"
          :row-height="GIRD_LAYOUT.ROW_HEIGHT"
          :is-draggable="$can('portal-cdp', 'cdp-portal-dashboard-settinggraph')"
          :is-resizable="$can('portal-cdp', 'cdp-portal-dashboard-settinggraph')"
          vertical-compact
          use-css-transforms
        >
          <grid-item
            v-for="(layoutItem, index) in layout"
            :key="index"
            v-intersection-observer="() => onSeen(index)"
            :static="layoutItem.static"
            :x="layoutItem.x"
            :y="layoutItem.y"
            :w="layoutItem.w"
            :h="layoutItem.h"
            :i="layoutItem.i"
            :id="'grid-' + layoutItem.i"
            @resized="onAdjustLayout"
            @moved="onAdjustLayout"
            :min-h="layoutItem.minH"
            :min-w="layoutItem.minW"
          >
            <analytics-chart-container
              v-if="layoutItem && seen[index]"
              class="w-100 h-100"
              :ref="`CustomChartContainerRef-${layoutItem.i}`"
              :width="layoutItem.w"
              :height="layoutItem.h"
              :chartLayoutId="Number(layoutItem.i)"
              :index="layoutItem.i"
              :source-list="sourceList"
              :main-start-date="startDate"
              :main-end-date="endDate"
              :main-relative-model="relativeModel"
              :is-main-filter-change="isMainFilterChanged"
              @on-click-data-info="onClickDataInfo"
              @on-click-setting-chart="onClickSettingChart"
              @on-click-delete="onClickDelete"
              @on-fetched="onCustomChartContainerFetched(Number(layoutItem.i))"
              :loading-data-info="loading.dataInfo"
              :loading-setting-chart="loading.settingChart"
              :loading-delete="loading.delete"
              :filter-list="filterList"
              :filter-chart-source-options="filterChartSourceOptions"
              :filter-chart-segment-options="filterChartSegmentOptions"
              @see-more-filter="onClickSeeMoreFilter"
            />
            <div v-else-if="!layoutItem">
              <empty-chart-icon />
            </div>
          </grid-item>
        </grid-layout>
        <div v-else class="bi-overview-no-item">
          <img src="@/assets/images/artwork/bi-no-item.png" alt="" draggable="false" />
          <div class="bi-overview-no-item-text">{{ t('bi.unlock_crucial_information') }}</div>
          <!-- TODO: Refactor btn to base-btn -->
          <div class="bi-action-wrp">
            <button class="btn btn-outline-primary d-flex align-items-center create-bi-button" @click="modalOpen.addAnalyticsChart = true">
              <div class="add-icon">
                <add-icon />
              </div>
              <div>{{ t('bi.add_chart') }}</div>
            </button>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="loading-layout">
          <loader-spinner />
        </div>
      </template>
    </div>
    <add-bi-modal
      v-if="modalOpen.addAnalyticsChart"
      :is-open="modalOpen.addAnalyticsChart"
      :module-id="chartModule.ANALYTICS"
      @close-modal="onCloseAddAnalyticsChartModal"
      @cancel="onCloseAddAnalyticsChartModal"
      @add-new-chart="addNewChartFromSelection"
    />
    <data-info-modal
      v-if="modalOpen.dataInfo"
      :is-open="modalOpen.dataInfo"
      :chart-name="chartDataInfoModalProps.chartName"
      :description="chartDataInfoModalProps.description"
      :filters="chartDataInfoModalProps.filters"
      :data-info="chartDataInfoModalProps.dataInfo"
      :chartDataInfoExportProps="chartDataInfoExportProps"
      @close-modal="onCloseDataInfoModal"
      @cancel="onCloseDataInfoModal"
    />
    <setting-chart-modal
      v-if="modalOpen.settingChart"
      :is-open="modalOpen.settingChart"
      :name="chartSettingModalProps.name"
      :type-selected="chartSettingModalProps.typeSelected"
      :type-list="chartSettingModalProps.typeList"
      :item-property="chartSettingModalProps.itemPropertyList"
      @close-modal="onCloseSettingChartModal"
      @cancel="onCloseSettingChartModal"
      @apply="onApplySetting"
    />
    <filter-selector-modal
      :filter-items-props="filterItemProps"
      :filter-options="filterChartSourceOptions"
      v-if="modalOpen.filterSelector"
      :is-open="modalOpen.filterSelector"
      @close-modal="onCloseFilterSelectorModal"
      @cancel="onCloseFilterSelectorModal"
      @apply-filter="onFilterModalApply"
    />
  </div>
</template>
<script setup lang="ts">
``;
// libs
import { useI18n } from 'vue-i18n';

//Component
import DateRangeDisplayInput from '@/views/components/date-display-input/DateRangeDisplayInput.vue';
import LoaderSpinner from '@/views/components/loader/loader-spinner.vue';
import { ChocoDateRangePicker } from '@choco-platform/components';
import AddBiModal from '../../components/bi-modal/AddBiModal.vue';
import DataInfoModal from '../../components/bi-modal/DataInfoModal.vue';
import SettingChartModal from '../../components/bi-modal/SettingChartModal.vue';
import FilterSelectorModal from './chart-churn-rate/chart-modal/FilterSelectorModal.vue';

//component chart
import AnalyticsChartContainer from '@/views/modules/analytics/components/anal-churnrate/chart-churn-rate/ChartChurnRateContainer.vue';
import EmptyChartIcon from '@/assets/icons/modules/analytics/bi/EmptyChartIcon.vue';
import ExportPDFButton from '@/views/modules/analytics/components/bi-button/ExportPDFButton.vue';

//directive
import vIntersectionObserver from '../../directives/directive';

//icon
import AddIcon from '@/assets/icons/base/button/AddIcon.vue';
import ResetIcon from '@/assets/icons/modules/analytics/bi/ResetIcon.vue';
import DownWardIcon from '@/assets/icons/base/v-select/DownWardIcon.vue';

//hook
import useOverViewAnal from './hooks/useOverViewAnal';

// Constants
import { CHART_MODULE as chartModule } from '@/constants/modules/bi/chart';

const { t } = useI18n();

const {
  GIRD_LAYOUT,
  seen,
  layout,
  loading,
  sourceListOptions,
  sourceList,
  filterChartSourceOptions,
  filterChartSegmentOptions,
  endDate,
  startDate,
  relativeModel,
  isMainFilterChanged,
  modalOpen,
  chartSettingModalProps,
  chartDataInfoModalProps,
  onCustomChartContainerFetched,
  onSeen,
  onAdjustLayout,
  onClickEditTab,
  onCloseEditTabItemModal,
  onClickDateSelect,
  onResetDate,
  onCloseAddAnalyticsChartModal,
  onCloseDataInfoModal,
  onCloseSettingChartModal,
  onCloseAddTabModal,
  onClickDataInfo,
  onClickSettingChart,
  onApplySetting,
  onClickDelete,
  addNewChartFromSelection,
  onClickApplyFilter,
  onCloseFilterSelectorModal,
  onClickSeeMoreFilter,
  onFilterModalApply,
  chartDataInfoExportProps,
  onExportPreview,
  filterList,
  filterItemProps,
} = useOverViewAnal();
</script>

<style lang="scss" scoped>
@import '@/assets/styles/base/grid.scss';
@import '@/assets/styles/modules/analytics/bi/analytics-bi.scss';
@import '@/assets/styles/modules/analytics/churn-rate/chart/overview-analytics.scss';
</style>
